import { normalizeHex } from './colorUtils';
import { extractPaintColors, normalizePaint } from './paintUtils';

const uniq = (arr) => Array.from(new Set(arr));

const normalizeColorList = (colors = [], limit = 24) => {
  const normalized = colors
    .map((color) => normalizeHex(color))
    .filter(Boolean);
  return uniq(normalized).slice(0, limit);
};

const normalizeGradientList = (gradients = [], limit = 12) => {
  const normalized = gradients
    .map((paint) => normalizePaint(paint))
    .filter((paint) => paint && paint.type === 'gradient');
  const seen = new Set();
  const results = [];
  normalized.forEach((paint) => {
    const key = JSON.stringify(paint);
    if (seen.has(key)) return;
    seen.add(key);
    results.push(paint);
  });
  return results.slice(0, limit);
};

export const normalizePalette = (palette = {}) => ({
  documentColors: normalizeColorList(palette.documentColors, 24),
  recentColors: normalizeColorList(palette.recentColors, 12),
  recentGradients: normalizeGradientList(palette.recentGradients, 12),
});

export const addRecentPaint = (palette, paint) => {
  const normalized = normalizePalette(palette);
  const safePaint = normalizePaint(paint);
  if (safePaint.type === 'gradient') {
    return {
      ...normalized,
      recentGradients: normalizeGradientList([safePaint, ...normalized.recentGradients], 12),
    };
  }
  return {
    ...normalized,
    recentColors: normalizeColorList([safePaint.color, ...normalized.recentColors], 12),
  };
};

export const collectDocumentColors = (elements = [], pageSettings) => {
  const counts = new Map();
  const bump = (hex) => {
    const normalized = normalizeHex(hex);
    if (!normalized) return;
    counts.set(normalized, (counts.get(normalized) || 0) + 1);
  };

  const backgroundPaint = pageSettings?.backgroundPaint;
  if (backgroundPaint) {
    extractPaintColors(backgroundPaint).forEach(bump);
  }

  elements.forEach((el) => {
    if (el.type === 'text') {
      extractPaintColors(el.style?.paint || el.paint || el.color).forEach(bump);
    }
    if (el.type === 'shape') {
      extractPaintColors(el.style?.fillPaint || el.fillPaint || el.fill).forEach(bump);
      if (el.stroke || el.style?.strokePaint) {
        extractPaintColors(el.style?.strokePaint || el.strokePaint || el.stroke).forEach(bump);
      }
    }
  });

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([color]) => color)
    .slice(0, 24);
};
